// src/views/auth/slice/actions.ts
import { AxiosError, AxiosResponse } from 'axios';
import { decode } from 'jsonwebtoken';
import { writeStorage, deleteFromStorage } from '@rehooks/local-storage';
import authSlice from './slice';
import studentSlice from '../../student/slice/slice';
import studentWordsListsSlice from '../../student-words-list/slice/slice';
import wordsListsSlice from '../../words-lists/slice/slice';
import wordsListSlice from '../../words-list/slice/slice';
import foldersSlice from '../../folders/slice/slice';
import { AppThunk } from '../../../redux/store';
import API from '../../../services/ApiService';
import {
  ActivateError, ApiError, SignInSuccess, UserData,
} from '../../../services/ApiService.types';
import { ACTIVITY_ENABLED, sendActivityAfterLogin, sendActivityBeforeLogout } from '../../../services/activity';
import { Roles } from '../../../constants/Roles';

const {
  startAuth, finishAuth, setAuthError, startActivation, setActivationError, finishActivation,
  removeAuthError, removeResetPasswordError, setUserData, wipeUserData,
  startSignUp, finishSignUp, setSignUpError, setActiveList,
} = authSlice.actions;

// Dane konta wczytane w tej karcie: uczniowie z grupami, listy, drzewo folderów (trzymane do 5 minut bez
// ponownego pobierania), otwarta lista i listy ucznia. Bez czyszczenia następna osoba zalogowana w tej samej
// karcie zobaczyłaby je, zanim przyjdą jej własne — np. na wspólnym komputerze w szkole
const clearAccountData = (): AppThunk => (dispatch) => {
  dispatch(studentSlice.actions.resetData());
  dispatch(studentWordsListsSlice.actions.resetData());
  dispatch(wordsListsSlice.actions.resetData());
  dispatch(wordsListSlice.actions.resetData());
  dispatch(foldersSlice.actions.resetFoldersState());
};

// Profil i aktywna lista siedzą w przeglądarce, wspólnej dla wszystkich kont — bez czyszczenia
// przeszłyby na następną osobę. Najpierw token: jego usunięcie zamyka gry, które bez listy padają.
// Jeszcze przed nim wysyłka prób z monitoringu aktywności — bez tokenu czekałyby na ponowne zalogowanie.
const logout = (): AppThunk => async (dispatch) => {
  sendActivityBeforeLogout();
  deleteFromStorage('token');
  dispatch(setActiveList(null));
  dispatch(wipeUserData());
  dispatch(clearAccountData());
};

const signIn = (emailInput: string, password: string): AppThunk => async (dispatch) => {
  dispatch(startAuth());
  try {
    const resp = await API.user.signIn(emailInput, password);
    const { data: { accessToken } } = resp as AxiosResponse<SignInSuccess>;
    const {
      id, email, surname, name, role,
    } = decode(accessToken) as UserData;

    // Na wypadek sesji zakończonej bez wylogowania, np. po wygaśnięciu tokenu. Przed zapisem tokenu,
    // bo ten od razu przenosi na stronę startową i menu mignęłoby grami poprzedniego konta.
    dispatch(setActiveList(null));
    dispatch(clearAccountData());
    writeStorage('token', accessToken);

    dispatch(setUserData({
      id, email, surname, name, role,
    }));

    // Niewysłane próby z monitoringu aktywności idą od razu. Sam useActivityQueue tego nie załatwi, gdy po wygaśnięciu
    // sesji loguje się ten sam uczeń — jego id się nie zmienia
    if (ACTIVITY_ENABLED && role === Roles.STUDENT) {
      sendActivityAfterLogin(id);
    }
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    deleteFromStorage('token');
    // Bez odpowiedzi (brak sieci) błąd i tak musi być niepusty, inaczej formularz niczego nie pokaże
    dispatch(setAuthError(response?.data || {}));
    dispatch(wipeUserData());
  } finally {
    dispatch(finishAuth());
  }
};

// `@me` to jedyne źródło `canCurate`. Pełny profil nadpisuje dane odczytane z tokena przy logowaniu.
// Gdy zapytanie padnie, zostaje to, co było — uprawnień do katalogu i tak pilnuje backend.
const fetchMe = (): AppThunk => async (dispatch) => {
  try {
    const { data } = await API.user.me();

    dispatch(setUserData(data));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    console.error('❌ Failed to fetch profile:', response?.data);
  }
};

const signUpAccount = (data: any): AppThunk => async (dispatch) => {
  dispatch(startSignUp());
  try {
    dispatch(logout());

    await API.user.signUp(data);
  } catch (e) {
    const { response } = e as AxiosError<any>;
    // Bez odpowiedzi (brak sieci) błąd i tak musi być niepusty, inaczej widok uznałby rejestrację za udaną
    dispatch(setSignUpError(response?.data || {}));
  } finally {
    dispatch(finishSignUp());
  }
};

const activateAccount = (token: string): AppThunk => (
  async (dispatch) => {
    dispatch(startActivation());
    try {
      dispatch(logout());

      await API.user.activateAccount(token);
    } catch (e) {
      const { response } = e as AxiosError<ActivateError>;
      // Bez odpowiedzi (brak sieci) błąd i tak musi być niepusty, inaczej widok uznałby aktywację za udaną
      dispatch(setActivationError(response?.data || {}));
    } finally {
      dispatch(finishActivation());
    }
  }
);

const activateStudentAccount = (token: string, password: string): AppThunk => (
  async (dispatch) => {
    dispatch(startActivation());
    try {
      dispatch(logout());

      await API.user.activateStudentAccount(token, password);
    } catch (e) {
      const { response } = e as AxiosError<ActivateError>;
      // Bez odpowiedzi (brak sieci) błąd i tak musi być niepusty, inaczej widok uznałby aktywację za udaną
      dispatch(setActivationError(response?.data || {}));
    } finally {
      dispatch(finishActivation());
    }
  }
);

export {
  activateAccount,
  activateStudentAccount,
  fetchMe,
  signUpAccount,
  logout,
  signIn,
  setActiveList,
  removeAuthError,
  removeResetPasswordError,
};
